import { FC, useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Tooltip from 'react-tooltip';
import { usePermission } from 'hooks';
import { RootState } from 'store';
import { openModal } from 'actions/modal';
import apiUser from 'api/user';
import { menuConfiguration, MenuItem } from 'constants/menuConfiguration';
import { SideBarMenuItem } from './components/SideBarMenuItem';
import { SideBarSubMenu } from './components/SideBarSubMenu';
import Animation from 'components/ui/animation';
import Link from 'components/ui/link';
import { RiveProvider } from './RiveProvider';
import './sideBarMenu.scss';
import { updateLayoutConfiguration } from 'actions/layoutConfiguration';

export const SideBarMenu: FC = () => {
  const dispatch = useDispatch();
  const { isEnabled } = usePermission();
  const history = useHistory();
  const location = useLocation();
  const isMenuExpanded = useSelector(
    (state: RootState) => state.layoutConfiguration.isMenuExpanded
  );
  const isNewMenuNotified = useSelector(
    (state: RootState) => state.layoutConfiguration.isNewMenuNotified
  );

  useEffect(() => {
    if (isNewMenuNotified) {
      return;
    }

    const setMenuNotified = async () => {
      try {
        await apiUser.setConfig({
          param: 'isNewMenuNotified',
          value: true,
        });

        dispatch(
          updateLayoutConfiguration({
            isNewMenuNotified: true,
          })
        );
      } catch (err) {
        console.error(err);
      }
    };

    dispatch(
      openModal({
        modalId: 'MenuVersionNotification',
        callback: async (result) => {
          setMenuNotified();

          if (result) {
            history.push({
              pathname: '/profile',
              state: { scrollToMenuVersion: true },
            });
          }
        },
      })
    );
  }, []);

  useEffect(() => {
    Tooltip.rebuild();
  }, [isMenuExpanded]);

  const { topMenuList, bottomMenuList } = useMemo(() => {
    const menuItems = menuConfiguration
      .map((item) => {
        const { children, permissions } = item;

        if (children) {
          const visibleChildren = children.filter(({ permissions }) =>
            permissions.some(isEnabled)
          );

          return visibleChildren.length > 0
            ? { ...item, children: visibleChildren }
            : null;
        }

        const hasAccess = permissions.some(isEnabled);

        return hasAccess ? item : null;
      })
      .filter((item) => item !== null);

    const hasAdmin = menuItems.at(-1)?.path === menuConfiguration.at(-1)?.path;

    return {
      topMenuList: hasAdmin ? menuItems.slice(0, -1) : menuItems,
      bottomMenuList: hasAdmin ? menuItems.slice(-1) : [],
    };
  }, []);

  const renderMenuItems = useCallback(
    (item: MenuItem, index: number) => {
      const {
        path,
        icon,
        title,
        children,
        customComponent: CustomComponent,
      } = item;

      if (children) {
        return (
          <li key={index} className='sidebar-menu__list-item'>
            <SideBarSubMenu title={title} icon={icon} children={children} />
          </li>
        );
      }

      const selected = location.pathname.includes(path);

      return (
        <li key={index} className='sidebar-menu__list-item'>
          <Link url={path}>
            {CustomComponent ? (
              <CustomComponent
                title={title}
                icon={icon}
                selected={selected}
                withTooltip
              />
            ) : (
              <SideBarMenuItem
                title={title}
                icon={icon}
                selected={selected}
                withTooltip
              />
            )}
          </Link>
        </li>
      );
    },
    [location.pathname]
  );

  const renderMenu = useMemo(() => {
    return (
      <RiveProvider>
        <ul className='sidebar-menu__list'>
          {topMenuList.map(renderMenuItems)}
        </ul>
        {bottomMenuList.length ? (
          <ul className='sidebar-menu__list sidebar-menu__bottom'>
            {bottomMenuList.map(renderMenuItems)}
          </ul>
        ) : null}
      </RiveProvider>
    );
  }, [renderMenuItems]);

  return (
    <Animation>
      <nav
        className={classNames('sidebar-menu', {
          'sidebar-menu_closed': !isMenuExpanded,
        })}>
        {renderMenu}
      </nav>
    </Animation>
  );
};
